/**
 * Turning a real failure into a test.
 *
 * Recognition is geometry, so a misread on an iPad is fully reproducible from the
 * strokes that caused it — but only if those strokes can leave the device. This
 * exports the current pad contents as JSON and imports them back, so the loop is:
 * write something on the iPad that reads wrong, export, paste into a fixture, and
 * the bug becomes a failing test that can be fixed on a laptop.
 *
 * The staff layout travels with the strokes. Without it the coordinates mean
 * nothing, because every position is measured relative to the staff spacing.
 */

import type { StaffLayout, Clef } from '../theory/staff';
import type { InkPoint, Stroke } from './types';

export const FIXTURE_VERSION = 1;

export interface StrokeFixture {
  version: number;
  /** Free text: what you wrote and what you expected it to read as. */
  note: string;
  clef: Clef;
  layout: StaffLayout;
  strokes: Stroke[];
  /** Recorded so a fixture can assert against what the engine said at the time. */
  readAs?: string[];
  createdAt: string;
}

export function exportFixture(
  strokes: Stroke[],
  layout: StaffLayout,
  clef: Clef,
  options: { note?: string; readAs?: string[] } = {},
): StrokeFixture {
  return {
    version: FIXTURE_VERSION,
    note: options.note ?? '',
    clef,
    layout,
    // Round coordinates: sub-pixel precision is noise, and it triples the file size.
    strokes: strokes.map((stroke) => ({
      ...stroke,
      points: stroke.points.map(roundPoint),
    })),
    readAs: options.readAs,
    createdAt: new Date().toISOString(),
  };
}

function roundPoint(point: InkPoint): InkPoint {
  return {
    x: Math.round(point.x * 100) / 100,
    y: Math.round(point.y * 100) / 100,
    pressure: Math.round(point.pressure * 1000) / 1000,
    tiltX: Math.round(point.tiltX),
    tiltY: Math.round(point.tiltY),
    t: Math.round(point.t),
  };
}

export function serializeFixture(fixture: StrokeFixture): string {
  return JSON.stringify(fixture, null, 2);
}

/**
 * Parse a fixture, rejecting anything malformed rather than letting a broken file
 * turn into a confusing recognition failure later.
 */
export function parseFixture(text: string): StrokeFixture {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    throw new Error('Not valid JSON.');
  }
  const candidate = raw as Partial<StrokeFixture>;

  if (typeof candidate !== 'object' || candidate === null) throw new Error('Not a fixture object.');
  if (candidate.version !== FIXTURE_VERSION) {
    throw new Error(`Unsupported fixture version ${String(candidate.version)}.`);
  }
  if (!candidate.layout || typeof candidate.layout.lineGap !== 'number') {
    throw new Error('Missing staff layout — stroke coordinates would be meaningless.');
  }
  if (!Array.isArray(candidate.strokes)) throw new Error('Missing strokes.');

  for (const stroke of candidate.strokes) {
    if (!Array.isArray(stroke.points) || stroke.points.length === 0) {
      throw new Error('A stroke has no points.');
    }
    for (const point of stroke.points) {
      if (typeof point.x !== 'number' || typeof point.y !== 'number') {
        throw new Error('A stroke point is missing coordinates.');
      }
    }
  }

  return candidate as StrokeFixture;
}

// ------------------------------------------------------- synthetic variations

/**
 * Real handwriting varies in ways that break naive recognisers, and it is hard to
 * collect enough of it by hand. These transforms take one recorded sample and
 * produce the awkward versions — written small, written large, written fast,
 * written at a slight angle — so a single real failure covers a family of them.
 *
 * The staff layout is transformed alongside the ink where it needs to be, so
 * "smaller writing" means smaller relative to the staff, which is what actually
 * matters to the recogniser.
 */

export function scaleStrokes(strokes: Stroke[], factor: number, originX = 0, originY = 0): Stroke[] {
  return strokes.map((stroke) => ({
    ...stroke,
    id: `${stroke.id}-s${factor}`,
    points: stroke.points.map((p) => ({
      ...p,
      x: originX + (p.x - originX) * factor,
      y: originY + (p.y - originY) * factor,
    })),
  }));
}

/** Rotate about a point, in degrees. People do not write perfectly level. */
export function rotateStrokes(strokes: Stroke[], degrees: number, originX: number, originY: number): Stroke[] {
  const radians = (degrees * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return strokes.map((stroke) => ({
    ...stroke,
    id: `${stroke.id}-r${degrees}`,
    points: stroke.points.map((p) => {
      const dx = p.x - originX;
      const dy = p.y - originY;
      return { ...p, x: originX + dx * cos - dy * sin, y: originY + dx * sin + dy * cos };
    }),
  }));
}

export function translateStrokes(strokes: Stroke[], dx: number, dy: number): Stroke[] {
  return strokes.map((stroke) => ({
    ...stroke,
    id: `${stroke.id}-t`,
    points: stroke.points.map((p) => ({ ...p, x: p.x + dx, y: p.y + dy })),
  }));
}

/**
 * Writing faster means fewer samples per unit of distance, because the digitiser
 * samples at a fixed rate. Dropping points is therefore a faithful model of speed,
 * and it is exactly the condition under which curve-fitting falls apart.
 */
export function resampleForSpeed(strokes: Stroke[], keepEvery: number): Stroke[] {
  return strokes.map((stroke) => {
    const kept = stroke.points.filter((_, i) => i % keepEvery === 0);
    const last = stroke.points[stroke.points.length - 1];
    if (kept[kept.length - 1] !== last) kept.push(last);
    return {
      ...stroke,
      id: `${stroke.id}-f${keepEvery}`,
      points: kept.map((p, i) => ({ ...p, t: i * 8 * keepEvery })),
    };
  });
}

/** Vary pressure, for engines that use it. Slow careful writing presses harder. */
export function withPressure(strokes: Stroke[], pressure: number): Stroke[] {
  return strokes.map((stroke) => ({
    ...stroke,
    id: `${stroke.id}-p${pressure}`,
    points: stroke.points.map((p) => ({ ...p, pressure })),
  }));
}
